import React, { FC, useEffect, useState } from 'react';
import { message } from 'antd';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';
import { api, endpoints } from '../../api';
import {
  BlueLayout,
  CustomButton,
  CustomGoBackButton,
  FormBlock,
  GridItem,
  PagesContainerUI,
} from '../../styles/styled';
import { RepresentativeType, UserDataResponseType } from '../../types';
import BookRegistrationComponent from '../NewApplicationPage/components/BookRegistrationComponent';
import {
  BookApplicationFormType,
  RulersType,
  RulerType,
  ValidatorRuleType,
  ValidatorsType,
} from '../NewApplicationPage/types';

type NewRepresentativeApplicationPageProps = {
  newRepresentativeApplication?: BookApplicationFormType;
  edit?: boolean;
};

export const NewRepresentativeApplicationPage: FC<NewRepresentativeApplicationPageProps> = ({
  edit,
  newRepresentativeApplication,
}) => {
  const navigate = useNavigate();
  const [rulersList, setRulersList] = useState<RulersType>([]);
  const [validatorState, setValidatorState] = useState<ValidatorsType>();
  const [publisherId, setPublisherId] = useState<number>();
  const [currentApplicationId, setApplicationId] = useState<string | null>(null);
  const { id } = useParams();
  const [applicationPostedState, setApplicationPosted] = useState<boolean>(false);

  useEffect(() => {
    if (id) setApplicationId(id);
  }, [id]);

  useEffect(() => {
    if (edit && newRepresentativeApplication?.line_of_textbooks)
      setRulersList(newRepresentativeApplication.line_of_textbooks);
  }, [edit, newRepresentativeApplication?.line_of_textbooks]);

  const registerApplication = () => {
    if (currentApplicationId) {
      api
        .put(endpoints.getApplicationById(currentApplicationId), rulersList)
        .then(() => {
          message.success('Заявление успешно отправлено!');
          navigate(-1);
        })
        .catch(() => message.error('Возникли проблемы при отправке заявления'));
    } else {
      message.success('Заявление успешно отправлено!');
      navigate(-1);
    }
  };

  const textBooksValidator = (rulers: RulersType) => {
    setValidatorState({});

    rulers
      .map((el) => el.textbooks)
      .map((ruler_textbooks, rulerIndex) =>
        ruler_textbooks.map((textbook, textbookIndex) => {
          const rules: ValidatorRuleType = {
            right_holder: textbook.right_holder.length > 0,
            fio: textbook.author.length > 0,
            subject_general: textbook?.subject_general?.id > 0,
            subject_area_general: textbook?.subject_general?.subject_area_general?.id > 0,
            title: textbook.title.length > 0,
            year: textbook.year.toString().length > 0,
            school_classes: textbook.school_classes.length > 0,
            file_textbook: !!textbook.file_textbook,
            file_guideline: !!textbook.file_guideline,
            file_instruction: !!textbook.file_instruction,
            text_book_level: !!textbook.text_book_level,
          };

          if (Object.values(rules).includes(false)) {
            setValidatorState((prevState) => {
              return (
                prevState && {
                  ...prevState,
                  [rulerIndex]: prevState[rulerIndex]
                    ? [...prevState[rulerIndex], { index: textbookIndex, ...rules }]
                    : [{ index: textbookIndex, ...rules }],
                }
              );
            });
          }

          return true;
        }),
      );
  };

  useEffect(() => {
    if (validatorState && Object.keys(validatorState).length === 0) {
      if (id) {
        api
          .put(endpoints.getApplicationById(id), {
            line_of_textbooks: rulersList,
            date_created: newRepresentativeApplication?.date_created
              ? newRepresentativeApplication?.date_created
              : moment().format('YYYY-MM-DD'),
          })
          .then(() => {
            message.success('Данные успешно обновлены');
            setApplicationPosted(true);
          });

        return;
      }

      api
        .post(endpoints.newApplication(), {
          line_of_textbooks: rulersList,
          status: { id: 6 },
          date_created: moment().format('YYYY-MM-DD'),
        })
        .then(() => {
          setApplicationPosted(true);
          message.success('Заявление успешно создано и имеет статус "Черновик"');
        })
        .catch(() => message.error('Произошла ошибка'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validatorState]);

  const updateRuler = (id: number, ruler: RulerType) => {
    const newRulers = [...rulersList];

    newRulers[id] = ruler;
    setRulersList(newRulers);
  };

  const removeRulerById = (id: number) => {
    const newRuler = [...rulersList];

    newRuler.splice(id, 1);
    setRulersList(newRuler);
  };

  useEffect(() => {
    (async () => {
      try {
        const idResponse = await api.get<UserDataResponseType>(endpoints.getUserData());

        if (idResponse) {
          const representativeResponse = await api.get<RepresentativeType>(
            endpoints.getRepresentativeById(idResponse?.data?.representative?.id),
          );

          setPublisherId(representativeResponse?.data?.publisher?.id);
        }
      } catch (err) {}
    })();
  }, []);

  return (
    <PagesContainerUI>
      <BlueLayout>
        <CustomGoBackButton onClick={() => navigate(-1)}>{'<'}</CustomGoBackButton>
        Заявление
      </BlueLayout>

      <FormBlock white>
        <GridItem align="flex-end">
          <div className="small-text">
            В Министерство просвещения Российской Федерации
            <br /> от test test test, <br />
            (Автономная некоммерческая организация «Образование»), <br />
            103062, г. Москва, ул. Макаренко, д. 5/16,
            <br /> +7(111)-111-11-11
          </div>
        </GridItem>

        <GridItem bottomBordered marginTop="25px" align="center">
          <div className="layout-text">ЗАЯВЛЕНИЕ</div>
          <div className="text">
            В соответствии с Порядком формирования федерального перечня учебников, допущенных к использованию при
            реализации имеющих государственную аккредитацию образовательных программ начального общего, основного
            общего, среднего общего образования, утвержденного приказом Министерства просвещения Российской Федерации от
            18.12.2019 г. № 695, для включения в федеральный перечень учебников прошу провести экспертизу
            прилагаемого(ых) учебника(ов).
          </div>
        </GridItem>

        {rulersList.length > 0 &&
          rulersList.map((el, index) => {
            return (
              <BookRegistrationComponent
                validated={validatorState ? validatorState[index] : undefined}
                ruler={el}
                textbooks={edit ? el.textbooks : undefined}
                updateRuler={(value) => updateRuler(index, value)}
                removeRulerById={() => removeRulerById(index)}
                key={index}
                id={index}
                publisherId={publisherId}
              />
            );
          })}

        <GridItem align="flex-start" justify="flex-start">
          <CustomButton
            width="350px"
            onClick={() => setRulersList([...rulersList, { title: 'Линейка 1', textbooks: [] }])}
          >
            добавить линейку учебников
          </CustomButton>
        </GridItem>
        <GridItem align="center" marginTop="20px" justify="center" gap="15px" wrap="nowrap" direction="row">
          <CustomButton
            onClick={() => {
              textBooksValidator(rulersList);
            }}
            width="350px"
          >
            Сохранить черновик
          </CustomButton>
          <CustomButton
            disable={!applicationPostedState}
            disabled={!applicationPostedState}
            onClick={() => registerApplication()}
            width="350px"
          >
            Подать заявление
          </CustomButton>
          {/*<CustomButton width="350px" lined>*/}
          {/*  Скрыть заявление*/}
          {/*</CustomButton>*/}
        </GridItem>
      </FormBlock>
    </PagesContainerUI>
  );
};
